import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BadgeCheck,
  BellOff,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  LogOut,
  Mail,
  Megaphone,
  Pencil,
  PhoneCall,
  Settings,
  Trash2,
  UserMinus,
  UserPlus,
  X,
  XCircle,
} from "lucide-react";
import { useMeusConvites, useResponderConvite } from "@/core/hooks/useConvites";
import type { ConviteMinisterio } from "@/core/services/convitesService";
import { ModalConfirmacao } from "@/shared/components/ModalConfirmacao";
import { useFecharComEsc } from "@/shared/hooks/useFecharComEsc";
import { useAuth } from "@/core/hooks/useAuth";
import { useUsuariosPendentes } from "@/core/hooks/useUsuarios";
import {
  useApagarNotificacao,
  useApagarTodasNotificacoes,
  useMarcarNotificacaoLida,
  useMarcarTodasNotificacoesLidas,
  useMinhasNotificacoes,
} from "@/core/hooks/useNotificacoes";
import type { Notificacao } from "@/core/services/notificacoesService";

/** Ícone por tipo de notificação (ARQ-04/BUG-03/BUG-05, migration 0034). */
const ICONE_NOTIFICACAO: Record<string, typeof Mail> = {
  reserva_aprovada: CheckCircle2,
  reserva_rejeitada: XCircle,
  reserva_criada_por_evento: CalendarClock,
  evento_criado_ministerio: CalendarDays,
  evento_atualizado_ministerio: Pencil,
  evento_excluido_ministerio: XCircle,
  // Comunicados (migration 0100): novo comunicado e aviso de 1 dia antes de apagar sozinho.
  comunicado_publicado: Megaphone,
  comunicado_apagar_amanha: CalendarClock,
  // Visitantes (migration 0101): visitante cadastrado e contato feito por líder de outro ministério.
  visitante_cadastrado: UserPlus,
  visitante_contato_lider: PhoneCall,
  // Vínculo com ministério (migration 0112). O convite não entra aqui: o sino já o lista
  // com Aceitar/Recusar (useMeusConvites) e a notificação dele existe só para o push.
  ministerio_funcao_concedida: BadgeCheck,
  ministerio_funcao_retirada: UserMinus,
  ministerio_saida: LogOut,
};

/** Tipo gravado só para disparar o push; o convite aparece no sino pelo cartão com Aceitar/Recusar. */
const TIPO_CONVITE_SO_PUSH = "ministerio_convite";

interface NotificationsPanelProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Painel do sino. Primeiro conteúdo REAL: convites de ministério pendentes
 * (DA-003) — a pessoa aceita ou recusa aqui mesmo (as amostras estáticas da
 * Fase 1 foram aposentadas). Outros tipos de aviso entram neste feed depois.
 */
export function NotificationsPanel({ open, onClose }: NotificationsPanelProps) {
  const navigate = useNavigate();
  useFecharComEsc(open, onClose);
  const { ehAdminPlataforma } = useAuth();
  const { data: convites, isLoading } = useMeusConvites();
  const { data: cadastrosPendentes } = useUsuariosPendentes();
  const { data: todasNotificacoes, isLoading: carregandoNotificacoes } = useMinhasNotificacoes();
  const notificacoes = todasNotificacoes?.filter((n) => n.tipo !== TIPO_CONVITE_SO_PUSH);
  const responder = useResponderConvite();
  const marcarLida = useMarcarNotificacaoLida();
  const marcarTodasLidas = useMarcarTodasNotificacoesLidas();
  const apagar = useApagarNotificacao();
  const apagarTodas = useApagarTodasNotificacoes();
  const [erro, setErro] = useState<string | null>(null);
  const [confirmandoExcluirTodas, setConfirmandoExcluirTodas] = useState(false);
  const [erroExcluirTodas, setErroExcluirTodas] = useState<string | null>(null);
  const [conviteParaRecusar, setConviteParaRecusar] = useState<ConviteMinisterio | null>(null);

  function abrirCadastrosPendentes() {
    onClose();
    navigate("/admin/usuarios");
  }

  async function handleClicarNotificacao(n: Notificacao) {
    if (!n.lida) marcarLida.mutate(n.id);
    if (n.link) {
      onClose();
      navigate(n.link);
    }
  }

  const temPendentes = ehAdminPlataforma && !!cadastrosPendentes?.length;
  const naoLidas = notificacoes?.filter((n) => !n.lida) ?? [];
  const semNadaParaMostrar =
    !isLoading && !carregandoNotificacoes && !convites?.length && !temPendentes && !notificacoes?.length;

  async function handleExcluirTodas() {
    setErroExcluirTodas(null);
    try {
      await apagarTodas.mutateAsync();
      setConfirmandoExcluirTodas(false);
    } catch (e) {
      setErroExcluirTodas(
        e instanceof Error ? e.message : "Não foi possível excluir as notificações.",
      );
    }
  }

  async function handleResponder(convite: ConviteMinisterio, aceitar: boolean) {
    setErro(null);
    try {
      await responder.mutateAsync({ vinculoId: convite.vinculoId, aceitar });
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível responder o convite.");
    } finally {
      setConviteParaRecusar(null);
    }
  }

  return (
    <>
      <div
        onClick={onClose}
        aria-hidden={!open}
        className={`fixed inset-0 z-30 bg-black/50 transition-opacity ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Notificações"
        className={`fixed bottom-0 left-0 top-0 z-40 flex w-[300px] max-w-[85vw] flex-col border-r border-border bg-surface transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-4">
          <h2 className="text-base font-bold text-text">Notificações</h2>
          <div className="flex items-center gap-1">
            {/* Frente 2, Fase 3: único ponto de entrada para /conta/notificacoes
                (preferências + push) — sem link em nenhum menu principal. */}
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate("/conta/notificacoes");
              }}
              aria-label="Configurar notificações"
              className="grid h-8 w-8 place-items-center rounded-lg text-muted hover:bg-surface-2 hover:text-text"
            >
              <Settings size={16} />
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              className="grid h-8 w-8 place-items-center rounded-lg text-muted hover:bg-surface-2 hover:text-text"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {erro && (
            <p role="alert" className="m-1 rounded-lg bg-danger/10 px-3 py-2 text-xs text-danger">
              {erro}
            </p>
          )}

          {isLoading ? (
            <p className="p-3 text-sm text-muted">Carregando...</p>
          ) : semNadaParaMostrar ? (
            <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-surface-2 text-muted">
                <BellOff size={18} />
              </span>
              <p className="text-sm text-muted">Nenhuma notificação por enquanto.</p>
            </div>
          ) : (
            <>
              {temPendentes && (
                <button
                  type="button"
                  onClick={abrirCadastrosPendentes}
                  className="flex w-full items-start gap-3 rounded-xl p-3 text-left hover:bg-surface-2"
                >
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-warning/10 text-warning">
                    <UserPlus size={18} />
                  </span>
                  <p className="text-sm font-medium leading-snug text-text">
                    <span className="font-bold">
                      {cadastrosPendentes!.length}{" "}
                      {cadastrosPendentes!.length === 1 ? "cadastro" : "cadastros"}
                    </span>{" "}
                    aguardando aprovação. Toque para revisar.
                  </p>
                </button>
              )}
              {convites?.map((c) => (
              <div key={c.vinculoId} className="rounded-xl p-3 hover:bg-surface-2">
                <div className="flex gap-3">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                    <Mail size={18} />
                  </span>
                  <p className="text-sm font-medium leading-snug text-text">
                    Você foi convidado(a) para o ministério{" "}
                    <span className="font-bold">{c.ministerioNome}</span> como{" "}
                    <span className="font-bold">{c.funcaoNome}</span>.
                  </p>
                </div>
                <div className="mt-2 flex gap-2 pl-12">
                  <button
                    type="button"
                    onClick={() => handleResponder(c, true)}
                    disabled={responder.isPending}
                    className="rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-on-primary transition-colors hover:bg-primary-hover disabled:opacity-60"
                  >
                    Aceitar
                  </button>
                  <button
                    type="button"
                    onClick={() => setConviteParaRecusar(c)}
                    disabled={responder.isPending}
                    className="rounded-lg border border-border px-3 py-1.5 text-xs font-bold text-muted transition-colors hover:bg-danger/10 hover:text-danger disabled:opacity-60"
                  >
                    Recusar
                  </button>
                </div>
              </div>
              ))}

              {!!notificacoes?.length && (
                <div className="mt-1">
                  {(!!convites?.length || temPendentes) && (
                    <div className="my-2 border-t border-border" />
                  )}
                  {/* Ações em lote (02/11/2026): "Excluir todas" aparece sempre que há notificação;
                      "Marcar todas como lidas" só quando há não lidas. */}
                  <div className="flex items-center justify-between gap-2 px-1 pb-1">
                    {naoLidas.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => marcarTodasLidas.mutate()}
                        disabled={marcarTodasLidas.isPending}
                        className="whitespace-nowrap py-1.5 text-xs font-semibold text-primary hover:underline disabled:opacity-60"
                      >
                        Marcar todas como lidas
                      </button>
                    ) : (
                      <span />
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setErroExcluirTodas(null);
                        setConfirmandoExcluirTodas(true);
                      }}
                      className="whitespace-nowrap py-1.5 text-xs font-semibold text-danger hover:underline"
                    >
                      Excluir todas
                    </button>
                  </div>
                  {notificacoes.map((n) => {
                    const Icone = ICONE_NOTIFICACAO[n.tipo] ?? Mail;
                    return (
                      <div
                        key={n.id}
                        className={`flex items-start rounded-xl hover:bg-surface-2 ${n.lida ? "opacity-60" : ""}`}
                      >
                        <button
                          type="button"
                          onClick={() => handleClicarNotificacao(n)}
                          className="flex min-w-0 flex-1 items-start gap-3 rounded-xl p-3 text-left"
                        >
                          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                            <Icone size={18} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <p className="text-sm font-semibold leading-snug text-text">{n.titulo}</p>
                            {n.mensagem && (
                              <p className="mt-0.5 text-xs leading-snug text-muted">{n.mensagem}</p>
                            )}
                          </span>
                          {!n.lida && (
                            <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden />
                          )}
                        </button>
                        {/* A32 (reteste 22-23/07/2026): apagar a notificação (lida ou não). */}
                        <button
                          type="button"
                          onClick={() => apagar.mutate(n.id)}
                          disabled={apagar.isPending}
                          aria-label="Excluir notificação"
                          className="mr-1 mt-2 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted transition-colors hover:bg-danger/10 hover:text-danger disabled:opacity-50"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </aside>

      <ModalConfirmacao
        aberto={confirmandoExcluirTodas}
        titulo="Excluir todas as notificações?"
        mensagem="Isso apaga todas as suas notificações, inclusive as mais antigas que não aparecem na lista. Não dá para desfazer."
        textoBotaoConfirmar="Excluir todas"
        textoBotaoCancelar="Cancelar"
        carregando={apagarTodas.isPending}
        erro={erroExcluirTodas}
        onConfirmar={() => void handleExcluirTodas()}
        onCancelar={() => setConfirmandoExcluirTodas(false)}
      />

      <ModalConfirmacao
        aberto={!!conviteParaRecusar}
        titulo="Recusar convite"
        mensagem={`Recusar o convite para o ministério "${conviteParaRecusar?.ministerioNome ?? ""}"? Você não entrará nele.`}
        textoBotaoConfirmar="Recusar"
        carregando={responder.isPending}
        onConfirmar={() => {
          if (conviteParaRecusar) void handleResponder(conviteParaRecusar, false);
        }}
        onCancelar={() => setConviteParaRecusar(null)}
      />
    </>
  );
}
